import React, { useState, useCallback } from "react";
import axios from "axios";
import {
  Sparkles, Search, Loader2, ShieldAlert, AlertTriangle, CheckCircle,
  Briefcase, Building2, Globe, Scale, Save, TrendingUp, Network, Gavel, Download
} from "lucide-react";
import { toast } from "sonner";
import { UnifiedSearchProgress } from "../components/UnifiedSearchProgress";

const API = `${window.location.origin}/api/unified`;

const LEVEL_COLORS = {
  critical: { bg: "rgba(220, 38, 38, 0.20)", border: "#dc2626", text: "#fca5a5" },
  high:     { bg: "rgba(239, 68, 68, 0.15)", border: "#ef4444", text: "#ef4444" },
  medium:   { bg: "rgba(245, 158, 11, 0.12)", border: "#f59e0b", text: "#f59e0b" },
  low:      { bg: "rgba(34, 197, 94, 0.10)",  border: "#22c55e", text: "#22c55e" },
};

const SOURCE_META = {
  mca21:         { label: "MCA21 Registry",      icon: Briefcase, color: "#8b5cf6" },
  gst:           { label: "GST Portal",          icon: Building2, color: "#06b6d4" },
  opensanctions: { label: "Sanctions Watch",     icon: Globe,     color: "#f43f5e" },
  ecourts:       { label: "Court Records",       icon: Scale,     color: "#f59e0b" },
  enforcement:   { label: "ED / CBI / SFIO",     icon: Gavel,     color: "#dc2626" },
};

function ScoreGauge({ score, level }) {
  const lvl = (level || "low").toLowerCase();
  const c = LEVEL_COLORS[lvl] || LEVEL_COLORS.low;
  return (
    <div style={{ background: c.bg, border: `2px solid ${c.border}`, borderRadius: "12px", padding: "20px 24px", textAlign: "center", minWidth: "200px" }}>
      <div style={{ color: c.text, fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1.5px" }}>OVERALL RISK</div>
      <div data-testid="risk-score" style={{ color: c.text, fontSize: "48px", fontWeight: 800, lineHeight: 1.1, margin: "4px 0" }}>{score ?? "—"}</div>
      <div data-testid="risk-level" style={{ color: c.text, fontSize: "14px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1px" }}>{(level || "UNKNOWN").toUpperCase()}</div>
    </div>
  );
}

function CrossPatternCard({ p }) {
  const c = LEVEL_COLORS[p.severity] || LEVEL_COLORS.medium;
  return (
    <div style={{ background: c.bg, border: `1px solid ${c.border}`, borderRadius: "8px", padding: "12px 16px", display: "flex", alignItems: "flex-start", gap: "12px" }}>
      <Network style={{ width: 18, height: 18, color: c.text, marginTop: "2px", flexShrink: 0 }} />
      <div style={{ flex: 1 }}>
        <div className="flex items-center gap-2 mb-1">
          <span style={{ background: c.border, color: "white", fontSize: "9px", padding: "2px 6px", borderRadius: "3px", fontWeight: 700, letterSpacing: "0.4px" }}>#{p.id}</span>
          <span style={{ color: c.text, fontSize: "13px", fontWeight: 700 }}>{p.title}</span>
          <span style={{ color: c.text, fontSize: "9px", marginLeft: "auto", textTransform: "uppercase", letterSpacing: "1px" }}>{p.severity}</span>
        </div>
        <div style={{ color: c.text, fontSize: "12px", lineHeight: 1.5 }}>{p.label}</div>
      </div>
    </div>
  );
}

function SourceCard({ name, data, expanded, onToggle }) {
  const meta = SOURCE_META[name];
  if (!meta) return null;
  const Icon = meta.icon;
  const flags = data?.risk?.flags || [];
  const sev = (data?.risk?.severity_level || "low").toLowerCase();
  const c = LEVEL_COLORS[sev] || LEVEL_COLORS.low;
  const r = data?.result || {};
  const sourceOk = r?._source_ok !== false;
  const sourceError = r?.error;

  // Per-source summary line (null-safe everywhere)
  let summary = "—";
  if (!sourceOk) {
    summary = "Source unavailable — partial results";
  } else if (name === "mca21") {
    const directors = r?.directors?.length ?? 0;
    const openCharges = (r?.charges || []).filter(ch => (ch?.status || "").toLowerCase() === "open").length;
    summary = `${r?.status ?? "?"} · ${directors} directors · ${openCharges} open charges`;
  } else if (name === "gst") {
    summary = `GSTIN ${r?.gstin_status ?? "?"} · ${r?.filing_compliance ?? "?"}`;
  } else if (name === "opensanctions") {
    summary = `${r?.total_matches ?? 0} match(es) · ${r?.is_sanctioned ? "SANCTIONED" : "Not sanctioned"}`;
  } else if (name === "ecourts") {
    summary = `${r?.total_cases ?? 0} cases · ${r?.pmla_fema_cases ?? 0} PMLA/FEMA · ${r?.criminal_cases ?? 0} criminal`;
  } else if (name === "enforcement") {
    const ed = r?.ed_data?.action_type ?? "None";
    const cbi = r?.cbi_data?.action_type ?? "None";
    const sfio = (r?.sfio_data?.companies_under_investigation?.length ?? 0) > 0 ? "Active" : "None";
    summary = `ED: ${ed} · CBI: ${cbi} · SFIO: ${sfio}`;
  }

  return (
    <div style={{ background: "#0d1117", border: `1px solid ${!sourceOk ? "#f59e0b" : (flags.length > 0 ? c.border : "#1e2530")}`, borderRadius: "10px", overflow: "hidden" }}>
      <div
        onClick={onToggle}
        style={{ padding: "14px 18px", display: "flex", alignItems: "center", gap: "12px", cursor: "pointer" }}
        data-testid={`source-card-${name}`}
      >
        <Icon style={{ width: 18, height: 18, color: sourceOk ? meta.color : "#f59e0b", flexShrink: 0, opacity: sourceOk ? 1 : 0.6 }} />
        <div style={{ flex: 1, overflow: "hidden" }}>
          <div style={{ color: "#f1f5f9", fontSize: "13px", fontWeight: 700 }}>{meta.label}</div>
          <div style={{ color: sourceOk ? "#94a3b8" : "#f59e0b", fontSize: "11px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={sourceError || ""}>{summary}</div>
        </div>
        {!sourceOk ? (
          <span data-testid={`source-status-${name}`} style={{ background: "rgba(245, 158, 11, 0.12)", border: "1px solid #f59e0b", color: "#f59e0b", fontSize: "10px", padding: "3px 8px", borderRadius: "4px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px" }}>
            Unavailable
          </span>
        ) : (
          <span style={{ background: c.bg, border: `1px solid ${c.border}`, color: c.text, fontSize: "10px", padding: "3px 8px", borderRadius: "4px", fontWeight: 700, textTransform: "uppercase" }}>
            {flags.length} flag{flags.length === 1 ? "" : "s"}
          </span>
        )}
        <span style={{ color: "#64748b", fontSize: "11px" }}>{expanded ? "▾" : "▸"}</span>
      </div>
      {expanded && (
        <div style={{ borderTop: "1px solid #1e2530", padding: "12px 18px", background: "#080c12" }}>
          {!sourceOk ? (
            <div style={{ color: "#f59e0b", fontSize: "12px", display: "flex", alignItems: "center", gap: 6 }}>
              <AlertTriangle style={{ width: 12, height: 12 }} />
              {sourceError || "This source was unavailable for this query. Retry from the search bar to refresh."}
            </div>
          ) : flags.length === 0 ? (
            <div style={{ color: "#22c55e", fontSize: "12px" }}>✓ Clean — no risk flags from this source</div>
          ) : (
            <div style={{ display: "grid", gap: "6px" }}>
              {flags.map((f, idx) => {
                const fc = LEVEL_COLORS[f.severity] || LEVEL_COLORS.medium;
                return (
                  <div key={idx} style={{ background: fc.bg, border: `1px solid ${fc.border}`, borderRadius: "6px", padding: "6px 10px", display: "flex", alignItems: "center", gap: "8px" }}>
                    <AlertTriangle style={{ width: 12, height: 12, color: fc.text }} />
                    <span style={{ color: fc.text, fontSize: "11px", fontWeight: 600 }}>{f.label}</span>
                    <span style={{ color: fc.text, fontSize: "9px", marginLeft: "auto", textTransform: "uppercase" }}>{f.severity}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function UnifiedSearchPage() {
  const [name, setName] = useState("");
  const [cin, setCin] = useState("");
  const [gstin, setGstin] = useState("");
  const [searching, setSearching] = useState(false);
  const [result, setResult] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [searchError, setSearchError] = useState(null);

  const handleSearch = useCallback(async () => {
    if (name.trim().length < 2) {
      toast.error("Enter a name (min 2 chars)");
      return;
    }
    setSearching(true);
    setResult(null);
    setJobId(null);
    setSearchError(null);
    try {
      // Kick off background job (short timeout — server returns job_id immediately)
      const { data: jobData } = await axios.post(
        `${API}/search`,
        { name, cin: cin || null, gstin: gstin || null },
        {
          withCredentials: true,
          timeout: 15000,
          headers: { "Content-Type": "application/json" },
        }
      );
      const newJobId = jobData.job_id;
      setJobId(newJobId);

      // Poll for completion (max ~3 minutes)
      const startTime = Date.now();
      const POLL_INTERVAL = 3000;
      const MAX_WAIT_MS = 180000;

      const poll = async () => {
        try {
          const { data: job } = await axios.get(
            `${API}/jobs/${newJobId}`,
            { withCredentials: true, timeout: 15000 }
          );
          if (job.status === "completed" && job.result) {
            setResult(job.result);
            const crossCount = job.result?.cross_patterns?.length ?? 0;
            const level = (job.result?.summary?.overall_level ?? "unknown").toUpperCase();
            const partial = !!job.result?.partial;
            if (partial) {
              const checked = job.result?.sources_checked ?? 0;
              const total = job.result?.sources_total ?? 5;
              toast.warning(`Partial results — ${checked}/${total} sources responded. Retry for full coverage.`);
            } else {
              toast.success(`Done — ${level} risk${crossCount > 0 ? ` · ${crossCount} cross-source pattern(s)` : ""}`);
            }
            const exp = {};
            Object.entries(job.result?.sources || {}).forEach(([k, v]) => {
              if ((v?.risk?.flags || []).length > 0 || v?.result?._source_ok === false) exp[k] = true;
            });
            setExpanded(exp);
            setSearching(false);
            return;
          }
          if (job.status === "failed") {
            const msg = job.error || "unknown error";
            setSearchError(msg);
            toast.error(`Search failed: ${msg}`);
            setSearching(false);
            return;
          }
        } catch (pollErr) {
          // Poll errors (network blip, 502) — keep retrying until MAX_WAIT
          console.error("[UnifiedSearch] poll error:", pollErr);
        }
        if (Date.now() - startTime > MAX_WAIT_MS) {
          setSearchError("Search timed out after 3 minutes. Some sources may be slow — please retry.");
          toast.error("Search timed out (3 min). Try again or use a shorter query.");
          setSearching(false);
          return;
        }
        // Still running — poll again
        setTimeout(poll, POLL_INTERVAL);
      };

      setTimeout(poll, POLL_INTERVAL);
    } catch (e) {
      const isTimeout = e?.code === "ECONNABORTED" || /timeout/i.test(e?.message || "");
      const isGateway = [502, 503, 504].includes(e?.response?.status);
      const msg = isTimeout || isGateway
        ? "Could not start the search — backend busy. Please retry."
        : (e.response?.data?.detail || "Search failed");
      setSearchError(msg);
      toast.error(msg);
      setSearching(false);
    }
  }, [name, cin, gstin]);

  const handleSave = async () => {
    if (!result) return;
    setSaving(true);
    try {
      await axios.post(`${API}/save`, result, { withCredentials: true });
      toast.success("Search saved to audit history");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleExportPDF = async () => {
    if (!jobId) {
      toast.error("Run a search first.");
      return;
    }
    setExporting(true);
    try {
      const res = await axios.get(`${API}/jobs/${jobId}/pdf`, {
        withCredentials: true,
        responseType: "blob",
        timeout: 60000,
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `rudrik-360-${result?.query?.name || jobId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF dossier downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PDF export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div data-testid="unified-search-page" style={{ color: "#f1f5f9" }}>
      <div className="flex items-center gap-3 mb-2">
        <Network style={{ width: 26, height: 26, color: "#2563eb" }} />
        <h1 style={{ fontSize: "26px", fontWeight: 700, margin: 0, color: "#f1f5f9" }} data-testid="page-title">360° Entity Search</h1>
      </div>
      <p style={{ color: "#94a3b8", fontSize: "13px", marginBottom: "24px" }}>
        Single query → 5 sources in parallel (MCA21, GST, OpenSanctions, eCourts, ED/CBI/SFIO) → cross-source pattern detection &amp; unified risk synthesis.
      </p>

      {/* Search panel */}
      <div style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: "12px", padding: "24px", marginBottom: "20px" }}>
        <div className="flex items-center gap-2 mb-4">
          <Sparkles style={{ width: 18, height: 18, color: "#2563eb" }} />
          <h2 style={{ fontSize: "16px", fontWeight: 700, margin: 0, color: "#f1f5f9" }}>Investigate Entity</h2>
        </div>

        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          placeholder="Entity name (person OR company) — e.g. 'Mehul Choksi', 'Amrapali Group'"
          data-testid="unified-name-input"
          style={{ width: "100%", background: "#080c12", border: "1px solid #1e2530", borderRadius: "8px", padding: "12px 16px", color: "#f1f5f9", fontSize: "14px", marginBottom: "10px" }}
        />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "12px" }}>
          <input
            type="text"
            value={cin}
            onChange={(e) => setCin(e.target.value.toUpperCase())}
            placeholder="CIN (optional — for precise MCA match)"
            data-testid="unified-cin-input"
            style={{ background: "#080c12", border: "1px solid #1e2530", borderRadius: "8px", padding: "10px 14px", color: "#f1f5f9", fontSize: "12px", fontFamily: "ui-monospace, monospace" }}
          />
          <input
            type="text"
            value={gstin}
            onChange={(e) => setGstin(e.target.value.toUpperCase())}
            placeholder="GSTIN (optional — for precise GST match)"
            data-testid="unified-gstin-input"
            style={{ background: "#080c12", border: "1px solid #1e2530", borderRadius: "8px", padding: "10px 14px", color: "#f1f5f9", fontSize: "12px", fontFamily: "ui-monospace, monospace" }}
          />
        </div>

        <div className="flex items-center justify-between">
          <div style={{ color: "#64748b", fontSize: "11px" }}>
            Parallel query: MCA21 · GST · OpenSanctions · eCourts · ED/CBI/SFIO · 10-30 seconds · Claude Sonnet 4.5
          </div>
          <button
            onClick={handleSearch}
            disabled={searching}
            data-testid="unified-search-btn"
            style={{
              background: searching ? "#1e2530" : "linear-gradient(135deg, #2563eb, #1d4ed8)",
              border: "none", borderRadius: "8px", color: "white", padding: "12px 28px",
              fontWeight: 700, fontSize: "13px", cursor: searching ? "wait" : "pointer",
              display: "flex", alignItems: "center", gap: "8px",
              boxShadow: searching ? "none" : "0 4px 12px rgba(37, 99, 235, 0.3)",
            }}
          >
            {searching ? (<><Loader2 className="animate-spin" style={{ width: 14, height: 14 }} /> Investigating across 5 sources...</>) : (<><Search style={{ width: 14, height: 14 }} /> Run 360° Search</>)}
          </button>
        </div>
      </div>

      {/* Live progress — shows during the ~15-25s background investigation */}
      <UnifiedSearchProgress isRunning={searching} isComplete={!!result && !searching} />

      {/* Error state — shown when search failed and there's no partial result to render */}
      {!result && searchError && !searching && (
        <div data-testid="search-error" style={{ background: "rgba(220, 38, 38, 0.10)", border: "1px solid #dc2626", borderRadius: "12px", padding: "20px 24px", marginBottom: "20px", display: "flex", alignItems: "center", gap: 14 }}>
          <ShieldAlert style={{ width: 22, height: 22, color: "#fca5a5", flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={{ color: "#fca5a5", fontSize: 14, fontWeight: 700 }}>Search failed</div>
            <div style={{ color: "#cbd5e1", fontSize: 12, marginTop: 4 }}>{searchError}</div>
          </div>
          <button onClick={handleSearch} data-testid="retry-search-btn"
                  style={{ background: "linear-gradient(135deg, #dc2626, #b91c1c)", border: "none", color: "white", borderRadius: 6, padding: "8px 16px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
            Retry
          </button>
        </div>
      )}

      {/* Partial-result banner */}
      {result?.partial && (
        <div data-testid="partial-banner" style={{ background: "rgba(245, 158, 11, 0.10)", border: "1px solid #f59e0b", borderRadius: "12px", padding: "12px 18px", marginBottom: "16px", display: "flex", alignItems: "center", gap: 12 }}>
          <AlertTriangle style={{ width: 16, height: 16, color: "#f59e0b" }} />
          <div style={{ flex: 1, color: "#fbbf24", fontSize: 12, fontWeight: 600 }}>
            Partial results — only {result?.sources_checked ?? 0} of {result?.sources_total ?? 5} sources responded.
            <span style={{ color: "#94a3b8", fontWeight: 500, marginLeft: 6 }}>
              Unavailable sources are marked below. Retry for full coverage.
            </span>
          </div>
          <button onClick={handleSearch} data-testid="retry-partial-btn"
                  style={{ background: "transparent", border: "1px solid #f59e0b", color: "#f59e0b", borderRadius: 6, padding: "6px 12px", fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
            Retry
          </button>
        </div>
      )}

      {/* Result */}
      {result && (
        <>
          {/* AI Synthesis hero — Rudrik 360° Intelligence Engine */}
          {result?.ai_synthesis && (() => {
            const ai = result.ai_synthesis;
            const decisionColor = {
              APPROVE: "#22c55e", CONDITIONS: "#84cc16",
              PENDING: "#f59e0b", REJECT: "#dc2626",
            }[ai?.decision] || "#94a3b8";
            const riskColor = LEVEL_COLORS[(ai?.overall_risk || "low").toLowerCase()] || LEVEL_COLORS.low;
            return (
              <div data-testid="ai-synthesis" style={{
                background: "#0d1117",
                border: `1px solid ${decisionColor}55`,
                borderRadius: 12, padding: 22, marginBottom: 16,
              }}>
                <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr 1fr 1fr", gap: 16, alignItems: "center" }}>
                  <div>
                    <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 }}>
                      AI Decision (Rudrik 360° Intelligence Engine)
                    </div>
                    <div data-testid="ai-decision" style={{ color: decisionColor, fontSize: 22, fontWeight: 800, marginTop: 4 }}>
                      {ai?.decision || "—"}
                    </div>
                    <div style={{ color: "#cbd5e1", fontSize: 12, marginTop: 6 }}>{ai?.summary || "—"}</div>
                    <div style={{ color: "#64748b", fontSize: 10, marginTop: 6 }}>
                      {ai?.ai_mode || "—"} · Sources {ai?.sources_ok ?? 0}/{(ai?.sources_ok ?? 0) + (ai?.sources_failed ?? 0)}
                    </div>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 }}>Risk</div>
                    <div data-testid="ai-risk-level" style={{ color: riskColor.text, fontSize: 18, fontWeight: 800, marginTop: 4 }}>
                      {(ai?.overall_risk || "LOW").toUpperCase()}
                    </div>
                    <div style={{ color: "#64748b", fontSize: 10 }}>score {ai?.risk_score ?? 0}</div>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 }}>CDD Tier</div>
                    <div data-testid="ai-cdd" style={{ color: "#f1f5f9", fontSize: 18, fontWeight: 800, marginTop: 4 }}>{ai?.cdd_tier || "—"}</div>
                    <div style={{ color: "#64748b", fontSize: 10 }}>{ai?.pep_found ? "PEP" : ai?.is_sanctioned ? "SANCTIONED" : "clean ID"}</div>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 }}>Flags / Emergent</div>
                    <div style={{ color: "#f1f5f9", fontSize: 18, fontWeight: 800, marginTop: 4 }}>
                      {(ai?.top_flags?.length || 0)} / {(ai?.emergent_risks?.length || 0)}
                    </div>
                    <div style={{ color: "#64748b", fontSize: 10 }}>top flags · emergent</div>
                  </div>
                </div>

                {(ai?.top_flags?.length || 0) > 0 && (
                  <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid #1e2530" }}>
                    <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 6 }}>Top Flags</div>
                    <div style={{ display: "grid", gap: 6 }}>
                      {(ai.top_flags || []).map((f, i) => {
                        const fc = LEVEL_COLORS[(f?.severity || "medium").toLowerCase()] || LEVEL_COLORS.medium;
                        return (
                          <div key={i} data-testid={`ai-flag-${i}`} style={{ background: fc.bg, border: `1px solid ${fc.border}`, borderRadius: 6, padding: "6px 10px", display: "flex", alignItems: "center", gap: 8 }}>
                            <AlertTriangle style={{ width: 12, height: 12, color: fc.text }} />
                            <span style={{ color: fc.text, fontSize: 12, fontWeight: 700 }}>{f?.flag}</span>
                            <span style={{ color: "#94a3b8", fontSize: 11, marginLeft: 6 }}>→ {f?.action}</span>
                            <span style={{ marginLeft: "auto", color: fc.text, fontSize: 9, fontWeight: 700, textTransform: "uppercase" }}>{f?.severity}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {(ai?.emergent_risks?.length || 0) > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 6 }}>Emergent Cross-Source Risks</div>
                    <div style={{ display: "grid", gap: 6 }}>
                      {(ai.emergent_risks || []).map((er, i) => (
                        <div key={i} data-testid={`ai-emergent-${i}`} style={{ background: "#080c12", border: "1px solid #a855f755", borderRadius: 6, padding: "6px 10px" }}>
                          <div style={{ color: "#c084fc", fontSize: 12, fontWeight: 700 }}>
                            {er?.pattern}
                            <span style={{ color: "#94a3b8", fontSize: 9, fontWeight: 700, marginLeft: 6, textTransform: "uppercase" }}>{er?.severity}</span>
                          </div>
                          <div style={{ color: "#cbd5e1", fontSize: 11, marginTop: 2 }}>{er?.finding}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Top summary */}
          <div style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: "12px", padding: "24px", marginBottom: "20px", display: "flex", alignItems: "center", gap: "24px" }}>
            <ScoreGauge score={result?.summary?.overall_score} level={result?.summary?.overall_level} />
            <div style={{ flex: 1 }}>
              <div style={{ color: "#64748b", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1.5px", marginBottom: "6px" }}>Recommendation</div>
              <div style={{ color: "#f1f5f9", fontSize: "16px", fontWeight: 700, marginBottom: "10px" }}>{result?.summary?.recommendation ?? "Awaiting analysis"}</div>
              <div style={{ color: "#94a3b8", fontSize: "12px" }}>
                Query: <strong style={{ color: "#cbd5e1" }}>{result?.query?.name ?? name ?? "—"}</strong>
                {result?.query?.cin && <span> · CIN {result.query.cin}</span>}
                {result?.query?.gstin && <span> · GSTIN {result.query.gstin}</span>}
              </div>
              <div style={{ color: "#64748b", fontSize: "11px", marginTop: "4px" }}>
                Sources queried: {result?.summary?.sources_count ?? result?.sources_checked ?? 0}
                {" · "}Cross-pattern bonus: +{result?.summary?.cross_pattern_bonus ?? 0}
                {result?.generated_at && (<> · {(() => { try { return new Date(result.generated_at).toLocaleString(); } catch { return result.generated_at; } })()}</>)}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleExportPDF}
                disabled={exporting || !jobId}
                data-testid="export-pdf-btn"
                style={{ background: "linear-gradient(135deg, #dc2626, #b91c1c)", border: "none", borderRadius: "8px", color: "white", padding: "10px 18px", fontSize: "12px", fontWeight: 700, cursor: exporting ? "wait" : "pointer", display: "flex", alignItems: "center", gap: "6px", boxShadow: "0 4px 12px rgba(220, 38, 38, 0.3)" }}
              >
                {exporting ? <Loader2 className="animate-spin" style={{ width: 12, height: 12 }} /> : <Download style={{ width: 12, height: 12 }} />}
                Export PDF Dossier
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                data-testid="save-unified-btn"
                style={{ background: "transparent", border: "1px solid #2563eb", borderRadius: "8px", color: "#2563eb", padding: "10px 18px", fontSize: "12px", fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: "6px" }}
              >
                {saving ? <Loader2 className="animate-spin" style={{ width: 12, height: 12 }} /> : <Save style={{ width: 12, height: 12 }} />}
                Save to History
              </button>
            </div>
          </div>

          {/* Cross-source patterns */}
          {result.cross_patterns?.length > 0 && (
            <div style={{ background: "#0d1117", border: "1px solid #dc2626", borderRadius: "12px", padding: "20px 24px", marginBottom: "20px" }}>
              <div className="flex items-center gap-2 mb-3">
                <ShieldAlert style={{ width: 18, height: 18, color: "#dc2626" }} />
                <h3 style={{ color: "#fca5a5", fontSize: "15px", fontWeight: 700, margin: 0 }}>
                  Cross-Source Patterns Detected ({result.cross_patterns.length})
                </h3>
              </div>
              <div style={{ color: "#94a3b8", fontSize: "11px", marginBottom: "12px" }}>
                These patterns combine signals across multiple sources. They are the highest-confidence fraud indicators.
              </div>
              <div style={{ display: "grid", gap: "8px" }}>
                {result.cross_patterns.map((p, idx) => <CrossPatternCard key={idx} p={p} />)}
              </div>
            </div>
          )}

          {result.cross_patterns?.length === 0 && (
            <div style={{ background: "rgba(34, 197, 94, 0.08)", border: "1px solid #22c55e", borderRadius: "10px", padding: "14px 20px", marginBottom: "20px", display: "flex", alignItems: "center", gap: "10px" }}>
              <CheckCircle style={{ width: 16, height: 16, color: "#22c55e" }} />
              <span style={{ color: "#22c55e", fontSize: "13px", fontWeight: 600 }}>No cross-source red flags detected.</span>
            </div>
          )}

          {/* Source cards */}
          <div style={{ display: "grid", gap: "10px" }}>
            {Object.entries(result.sources).map(([key, val]) => (
              <SourceCard
                key={key}
                name={key}
                data={val}
                expanded={!!expanded[key]}
                onToggle={() => setExpanded((e) => ({ ...e, [key]: !e[key] }))}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
